import {
  AccountGroup,
  type AccountInfo,
  Card,
  Col,
  Flex,
  Row,
  Space,
  Text,
} from '@/components';
import { t } from '@/i18n';
import type { ComponentProps, FC } from 'react';
import invariant from 'tiny-invariant';

import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import type { workflowStatusType } from '@/features/workflow/models/workflow';
import type { z } from 'zod';
import { WorkflowActionRequiredBadge } from '../../common/WorkflowActionRequiredBadge';
import { WorkflowStatusTag } from '../../common/WorkflowStatusTag';

type Props = {
  id: string;
  title: string;
  status: z.infer<typeof workflowStatusType>;
  isActionRequired: boolean;
  deadline: string | null;
  createdDate: string;
  assigneeAttributes: Array<ComponentProps<typeof AccountInfo>>;
};

export const WorkflowCard: FC<Props> = ({
  id,
  title,
  status,
  isActionRequired,
  deadline,
  createdDate,
  assigneeAttributes,
}) => {
  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(siderConfig?.viewType === 'workflow-list', t('unreachable-code'));

  const goToDetail = () => {
    stackSiderView.push({
      payload: {
        config: {
          ...siderConfig,
          viewType: 'workflow-details',
          stageViewType: 'list',
          id,
        },
      },
      target: stackSiderView.target,
    });
  };

  return (
    <Card bordered={false} hoverable onClick={goToDetail} size="small">
      <Flex vertical>
        <Space>
          {isActionRequired && <WorkflowActionRequiredBadge />}
          <WorkflowStatusTag status={status} />
        </Space>

        <Text variant="h4" text={title} />

        <Flex justify="space-between" align="center">
          <Row>
            <Col span={24}>
              <Row>
                <Col span={8}>
                  <Text
                    variant="footnote"
                    color="secondary"
                    text={t('deadline-date')}
                  />
                </Col>
                <Col>
                  <Text variant="footnote" color="secondary" text={deadline} />
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                <Col span={8}>
                  <Text
                    variant="footnote"
                    color="secondary"
                    text={t('created-date')}
                  />
                </Col>
                <Col>
                  <Text
                    variant="footnote"
                    color="secondary"
                    text={createdDate}
                  />
                </Col>
              </Row>
            </Col>
          </Row>

          <AccountGroup accountInfoList={assigneeAttributes} />
        </Flex>
      </Flex>
    </Card>
  );
};
